import { Component, OnDestroy, OnInit, ChangeDetectorRef } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { AuthService } from '../../core/services/auth.service';
import { STORAGE_NAME } from '../../core/constants/constants';
import { CookieService } from '@app/core/services/cookie.service';
import { ThemeService } from '@app/core/services/theme.service';
import { NzMessageService } from 'ng-zorro-antd/message';
import { DashboardService } from './services/dashboard.service';
import { PINNED_SYSTEMS_STORAGE_KEY, SystemViewModel, SystemCategory, DEFAULT_SYSTEM_CATEGORIES } from './models/system-view.model';
import { mapApiProjectToViewModel, formatLoginTime } from './utils/dashboard.utils';
import { environment } from '../../../environments/environment';
import { parseTextWithLinks, TextToken } from '@app/core/utils/utils';

export interface MyUserAccountDto {
  userAccountId: number;
  projectName: string;
  projectClientId: string;
  systemLoginName: string;
  password?: string;
  accountDescription?: string;
  usageGuide?: string;
  usageGuideLink?: boolean;
}

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  private subs: Subscription[] = [];
  private searchSubject = new Subject<string>();
  private pinnedSystemIds = new Set<string>();

  fullName = '';
  loginName = '';
  employeeCode = '';
  isAdmin = false;
  isStudentManager = false;

  fullSystemList: SystemViewModel[] = [];
  filteredSystemList: SystemViewModel[] = [];
  searchKeyword = '';
  selectedCategory = 'all';
  systemCategories: SystemCategory[] = [...DEFAULT_SYSTEM_CATEGORIES];
  private projectGroupsFromApi: Array<{ groupId: number; name: string }> = [];

  loading = false;
  sidebarOpen = false;
  showThemeModal = false;
  showAddAppModal = false;
  showMyAccountsModal = false;
  showSupportContactModal = false;
  myAccountsLoading = false;
  myAccounts: MyUserAccountDto[] = [];
  visibleMyPasswordIds = new Set<number>();
  lastLoginTime = '';
  currentYear = new Date().getFullYear();

  constructor(
    private authService: AuthService,
    private dashboardService: DashboardService,
    private message: NzMessageService,
    private cookieService: CookieService,
    private router: Router,
    private themeService: ThemeService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.loadUserInfo();
    this.loadPinnedSystems();

    this.authService.hasAnyRole(['ADMIN']).then(isAdmin => {
      this.isAdmin = isAdmin;
      this.cdr.markForCheck();
    });
    this.authService.hasAnyRole(['STUDENT_MANAGER']).then(isStudentManager => {
      this.isStudentManager = isStudentManager;
      this.cdr.markForCheck();
    });

    this.reloadDashboardData();
    this.loadLastLogin();

    this.subs.push(
      this.router.events
        .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
        .subscribe((event) => {
          const path = event.urlAfterRedirects.split('?')[0];
          if (path === '/' || path === '' || path === '/my-systems') {
            this.reloadDashboardData();
            this.loadLastLogin();
          }
          this.cdr.markForCheck();
        })
    );

    this.subs.push(
      this.searchSubject.pipe(debounceTime(300), distinctUntilChanged()).subscribe(keyword => {
        this.searchKeyword = keyword;
        this.refreshFilteredList();
      })
    );
  }

  get pinnedSystems(): SystemViewModel[] {
    return this.fullSystemList.filter(system => system.isPinned);
  }

  get unpinnedSystems(): SystemViewModel[] {
    return this.fullSystemList.filter(system => !system.isPinned);
  }

  get userInitial(): string {
    const name = this.fullName || this.loginName;
    if (!name) {
      return 'U';
    }
    const parts = name.trim().split(/\s+/);
    return parts[parts.length - 1].charAt(0).toUpperCase();
  }

  get isHomePage(): boolean {
    const path = this.router.url.split('?')[0];
    return path === '/' || path === '';
  }

  get isMySystemsPage(): boolean {
    return this.router.url.split('?')[0] === '/my-systems';
  }

  get showChildRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return !['/', '', '/my-systems'].includes(path);
  }

  get currentTheme(): 'light' | 'dark' {
    return this.themeService.getTheme();
  }

  onSearchChange(keyword: string): void {
    this.searchSubject.next(keyword);
  }

  onCategoryChange(categoryId: string): void {
    this.selectedCategory = categoryId;
    this.refreshFilteredList();
  }

  toggleSidebar(): void {
    this.sidebarOpen = !this.sidebarOpen;
  }

  closeSidebar(): void {
    this.sidebarOpen = false;
  }

  openThemeModal(): void {
    this.showThemeModal = true;
  }

  closeThemeModal(): void {
    this.showThemeModal = false;
  }

  openAddAppModal(): void {
    this.showAddAppModal = true;
  }

  closeAddAppModal(): void {
    this.showAddAppModal = false;
  }

  openMyAccountsModal(): void {
    this.showMyAccountsModal = true;
    this.visibleMyPasswordIds.clear();
    this.loadMyAccounts();
  }

  closeMyAccountsModal(): void {
    this.showMyAccountsModal = false;
  }

  openSupportContactModal(): void {
    this.showSupportContactModal = true;
  }

  closeSupportContactModal(): void {
    this.showSupportContactModal = false;
  }

  toggleMyPasswordVisibility(row: MyUserAccountDto): void {
    if (this.visibleMyPasswordIds.has(row.userAccountId)) {
      this.visibleMyPasswordIds.delete(row.userAccountId);
    } else {
      this.visibleMyPasswordIds.add(row.userAccountId);
    }
  }

  isMyPasswordVisible(row: MyUserAccountDto): boolean {
    return this.visibleMyPasswordIds.has(row.userAccountId);
  }

  isUsageGuideLink(value?: string): boolean {
    if (!value?.trim()) {
      return false;
    }
    return /^https?:\/\//i.test(value.trim());
  }

  parseTextWithLinks(value?: string): TextToken[] {
    return parseTextWithLinks(value);
  }

  trackBySystemId(_: number, system: SystemViewModel): string {
    return system.id;
  }

  trackByMyAccountId(_: number, row: MyUserAccountDto): number {
    return row.userAccountId;
  }

  pinFromModal(systemId: string): void {
    const system = this.fullSystemList.find(item => item.id === systemId);
    if (!system || system.isPinned) {
      return;
    }
    this.setPinned(system, true);
    this.message.success(`Đã thêm "${system.name}" vào truy cập nhanh.`);
  }

  togglePin(systemId: string, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    const system = this.fullSystemList.find(item => item.id === systemId);
    if (!system) {
      return;
    }
    this.setPinned(system, !system.isPinned);
  }

  setTheme(theme: 'light' | 'dark'): void {
    this.themeService.setTheme(theme);
    this.message.success(theme === 'dark' ? 'Đã chuyển sang giao diện tối.' : 'Đã chuyển sang giao diện sáng.');
  }

  logout(): void {
    this.authService.logout();
  }

  ngOnDestroy(): void {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  private loadUserInfo(): void {
    const userInfo = this.cookieService.get(STORAGE_NAME.USER_LOGIN);
    if (!userInfo) {
      return;
    }
    try {
      const parsed = JSON.parse(userInfo);
      this.fullName = parsed?.fullName ?? '';
      this.loginName = parsed?.loginName ?? '';
      this.employeeCode = parsed?.employeeCode ?? '';
      if (!this.fullName) {
        this.fullName = this.loginName;
      }
    } catch {
      this.fullName = '';
      this.loginName = '';
      this.employeeCode = '';
    }
  }

  private loadPinnedSystems(): void {
    const pinnedIdsString = this.cookieService.get(PINNED_SYSTEMS_STORAGE_KEY);
    if (!pinnedIdsString) {
      return;
    }
    try {
      this.pinnedSystemIds = new Set(JSON.parse(pinnedIdsString));
    } catch {
      this.pinnedSystemIds = new Set();
    }
  }

  private savePinnedSystems(): void {
    this.cookieService.set(
      PINNED_SYSTEMS_STORAGE_KEY,
      JSON.stringify(Array.from(this.pinnedSystemIds)),
      365
    );
  }

  private loadLastLogin(): void {
    this.dashboardService.getLastLogin().subscribe({
      next: (res: any) => {
        const raw = res?.data?.lastLoginTime;
        this.lastLoginTime = formatLoginTime(raw);
        this.cdr.markForCheck();
      },
      error: () => {
        this.lastLoginTime = '';
      },
    });
  }

  private loadMyAccounts(): void {
    this.myAccountsLoading = true;
    this.subs.push(
      this.dashboardService.getMyAccounts().subscribe({
        next: (res: any) => {
          this.myAccounts = (res?.data ?? []).map((row: MyUserAccountDto) => ({
            ...row,
            usageGuideLink: this.isUsageGuideLink(row.usageGuide),
          }));
          this.myAccountsLoading = false;
        },
        error: () => {
          this.myAccountsLoading = false;
          this.message.error('Không thể tải danh sách tài khoản hệ thống.');
        },
      })
    );
  }

  private reloadDashboardData(): void {
    this.loadProjectGroups();
    this.loadProjects();
  }

  private loadProjectGroups(): void {
    this.dashboardService.getProjectGroups().subscribe({
        next: (res: any) => {
          this.projectGroupsFromApi = (res?.data ?? [])
            .map((item: any) => ({
              groupId: item.groupId ?? item.group_id,
              name: item.name,
            }))
            .filter((item: { groupId: number; name: string }) => item.groupId != null);
          this.rebuildSystemCategories();
          this.cdr.markForCheck();
        },
        error: () => {
          this.projectGroupsFromApi = [];
          this.rebuildSystemCategories();
          this.cdr.markForCheck();
        },
      });
  }

  private rebuildSystemCategories(): void {
    const categories: SystemCategory[] = [{ id: 'all', label: 'Tất cả' }];
    const seen = new Set<string>();

    console.log(this.projectGroupsFromApi);
    
    for (const group of this.projectGroupsFromApi) {
      const id = String(group.groupId);
      if (!seen.has(id)) {
        seen.add(id);
        categories.push({ id, label: group.name });
      }
    }

    for (const system of this.fullSystemList) {
      if (system.groupId == null) {
        continue;
      }
      const id = String(system.groupId);
      if (!seen.has(id)) {
        seen.add(id);
        categories.push({ id, label: system.groupName || `Nhóm ${id}` });
      }
    }

    this.systemCategories = categories;

    if (this.selectedCategory !== 'all' && !seen.has(this.selectedCategory)) {
      this.selectedCategory = 'all';
    }
  }

  private loadProjects(): void {
    this.loading = true;
    this.dashboardService.getAllProjects().subscribe({
        next: (res: any) => {
          const items = res?.data ?? [];
          this.fullSystemList = items
            .map((item: any) => mapApiProjectToViewModel(item, this.pinnedSystemIds, environment.clientId))
            .filter((item: SystemViewModel | null): item is SystemViewModel => !!item);
          this.rebuildSystemCategories();
          this.refreshFilteredList();
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.message.error('Không thể tải danh sách ứng dụng.');
        },
      });
  }

  private setPinned(system: SystemViewModel, pinned: boolean): void {
    system.isPinned = pinned;
    if (pinned) {
      this.pinnedSystemIds.add(system.id);
    } else {
      this.pinnedSystemIds.delete(system.id);
    }
    this.savePinnedSystems();
    this.refreshFilteredList();

    this.dashboardService.toggleFavorite(system.id, pinned).subscribe({
      error: () => {
        // Giữ optimistic UI
      },
    });
  }

  private refreshFilteredList(): void {
    const keyword = this.searchKeyword.toLowerCase().trim();
    let list = [...this.fullSystemList];

    if (this.selectedCategory !== 'all') {
      list = list.filter(system => String(system.groupId ?? '') === this.selectedCategory);
    }

    if (keyword) {
      list = list.filter(
        system =>
          system.name.toLowerCase().includes(keyword) ||
          system.description.toLowerCase().includes(keyword) ||
          (system.homeRedirectUri || '').toLowerCase().includes(keyword)
      );
    }

    list.sort((a, b) => this.compareSystems(a, b));

    this.filteredSystemList = list;
  }

  private compareSystems(a: SystemViewModel, b: SystemViewModel): number {
    const timeA = a.lastAccessTime ? new Date(a.lastAccessTime).getTime() : 0;
    const timeB = b.lastAccessTime ? new Date(b.lastAccessTime).getTime() : 0;
    if (timeA !== timeB) {
      return timeB - timeA;
    }
    return a.name.localeCompare(b.name);
  }
}
